/**
 * Routing hint for the n8n `update_campaign` action. A single save can touch
 * several categories at once (e.g. a contact edit plus a status change); the
 * workflow still needs ONE deterministic value to branch on, so categories are
 * ranked by how specific their handler is:
 *
 *   contact_details  — any person/company field edit (needs lvl1 resolution)
 *   lead_status      — sales status/substatus change (stepper save)
 *   notes            — notes-only change
 *   snapshot         — no diff available / nothing categorizable changed
 *
 * The rich `updated` booleans in the same payload still tell the workflow what
 * else changed in a mixed save; `sub_action` only picks the entry door.
 */
export type ISRSubAction = 'contact_details' | 'lead_status' | 'notes' | 'snapshot';

const SALES_FIELD_PATTERN = /^(person|company)\.(lead_status|lead_substatus)$/;

const isSalesField = (field: string): boolean => {
  const normalized = field.trim().toLowerCase();
  return normalized === 'lead_status' ||
    normalized === 'lead_substatus' ||
    normalized === 'status' ||
    normalized === 'substatus' ||
    SALES_FIELD_PATTERN.test(normalized);
};

const isContactField = (field: string): boolean => {
  const normalized = field.trim().toLowerCase();
  if (SALES_FIELD_PATTERN.test(normalized)) return false;
  return normalized.startsWith('person.') || normalized.startsWith('company.');
};

export const deriveISRSubAction = (
  changedFields: string[] | undefined,
): ISRSubAction => {
  if (!changedFields || changedFields.length === 0) return 'snapshot';
  if (changedFields.some(isContactField)) return 'contact_details';
  if (changedFields.some(isSalesField)) return 'lead_status';
  if (changedFields.some((field) => field.trim().toLowerCase() === 'notes')) return 'notes';
  return 'snapshot';
};
